import { isOtherPrevBrandAnswerComplete } from '@/lib/otherPrevBrandAnswerStorage';
import { isOtherPrevBrandComplete } from '@/lib/otherPrevBrandStorage';
import { isLdProductNowComplete } from '@/lib/ldProductNowStorage';
import { isPrevBrandAnswerComplete, readPrevBrandAnswerAnswers } from '@/lib/prevBrandAnswerStorage';
import { isPrevBrandComplete } from '@/lib/prevBrandStorage';
import { isSetAProfileComplete } from '@/lib/setAProfileStorage';
import { isSetBLdBrandOpinionComplete } from '@/lib/setBLdBrandOpinionStorage';
import { isWhyNotLdComplete } from '@/lib/whyNotLdStorage';

export type QuestionnaireSet = 'A' | 'B';

/** Invalid questionnaire set → placeholder survey route. */
export function surveyQuestionnaireRedirect(questionnaireSet: unknown): '/survey' | null {
  return questionnaireSet === 'A' || questionnaireSet === 'B' ? null : '/survey';
}

/** Profile required for submit; Set A collects it, Set B hydrates from first submission on OTP verify. */
export function surveyProfileRedirect(questionnaireSet: unknown): string | null {
  if (questionnaireSet !== 'A' && questionnaireSet !== 'B') return null;
  if (isSetAProfileComplete()) return null;
  if (questionnaireSet === 'A') return '/survey/set-a/profile';
  return '/phone';
}

/** Redirect to the correct survey step for Set A / Set B, or null if flow is complete / waiting on next page. */
export function surveyStepRedirect(questionnaireSet: QuestionnaireSet): string | null {
  const profileRedirect = surveyProfileRedirect(questionnaireSet);
  if (profileRedirect) return profileRedirect;

  if (questionnaireSet === 'B') {
    if (!isLdProductNowComplete()) return '/ldproductnow';
    const smoked = readPrevBrandAnswerAnswers().smokedOtherProduct;
    if (smoked === 'yes') {
      if (!isOtherPrevBrandComplete()) return '/survey/prevbrand-other';
      if (!isOtherPrevBrandAnswerComplete()) return '/survey/prevbrandanswer-other';
    } else if (smoked === 'no' && !isSetBLdBrandOpinionComplete()) {
      return '/survey/set-b/brand-opinion';
    }
    return null;
  }

  if (!isPrevBrandComplete()) return '/survey/prevbrand';
  if (!isPrevBrandAnswerComplete()) return '/survey/prevbrandanswer';
  if (readPrevBrandAnswerAnswers().smokedOtherProduct === 'no' && !isWhyNotLdComplete()) {
    return '/whynotld';
  }
  if (readPrevBrandAnswerAnswers().smokedOtherProduct === 'yes') {
    if (!isOtherPrevBrandComplete()) return '/survey/prevbrand-other';
    if (!isOtherPrevBrandAnswerComplete()) return '/survey/prevbrandanswer-other';
  }
  return null;
}

/** Guards before the “other product” brand picker (Set A vs Set B entry differs). */
export function surveyBeforeOtherProductRedirect(questionnaireSet: QuestionnaireSet): string | null {
  const profileRedirect = surveyProfileRedirect(questionnaireSet);
  if (profileRedirect) return profileRedirect;

  if (questionnaireSet === 'B') {
    if (!isLdProductNowComplete()) return '/ldproductnow';
    if (readPrevBrandAnswerAnswers().smokedOtherProduct !== 'yes') return '/ldproductnow';
    return null;
  }

  if (!isPrevBrandComplete()) return '/survey/prevbrand';
  if (!isPrevBrandAnswerComplete()) return '/survey/prevbrandanswer';
  if (readPrevBrandAnswerAnswers().smokedOtherProduct !== 'yes') {
    return '/survey/prevbrandanswer';
  }
  return null;
}

export type { QuestionnaireSet };

export function isQuestionnaireSet(value: unknown): value is QuestionnaireSet {
  return value === 'A' || value === 'B';
}
